<template>
  <Card class="loadboard-card">
    <template #content>
      <div class="loadboard-card-header">
        <div v-if="sourceImage" class="loadboard-card-logo-wrapper">
          <img
            :src="sourceImage"
            :alt="loadboard.source ?? ''"
            class="loadboard-card-logo"
            :class="{
              'loadboard-card-logo--dat':
                loadboard.source === LoadboardSource.DAT,
              'loadboard-card-logo--truckstop':
                loadboard.source === LoadboardSource.TRUCKSTOP,
            }"
          />
        </div>

        <KeyValuePair
          class="loadboard-card-expires"
          :value="
            loadboard.expiration
              ? d(new Date(loadboard.expiration), 'numeric')
              : '-'
          "
          inline
        >
          <template #label>
            <i class="pi pi-calendar" />
            {{ t('common.expiration') }}:
          </template>
        </KeyValuePair>

        <RygenButton
          v-if="loadboard.source"
          v-tooltip.top="{
            value: removeTooltip,
            pt: { text: { style: 'white-space: nowrap' } },
          }"
          class="loadboard-card-remove"
          mode="tertiary"
          icon="pi pi-times"
          size="large"
          :severity="Severity.DANGER"
          @click="emit('remove', loadboard.source)"
        />
      </div>

      <div class="loadboard-card-details">
        <KeyValuePair
          :value="loadboard.mode ? translateEnum(loadboard.mode, 'Mode') : '-'"
          uppercase-label
        >
          <template #label>
            <i class="pi pi-truck" />
            {{ t('shipments.modes') }}
          </template>
        </KeyValuePair>
        <KeyValuePair :value="loadboard.equipmentType?.name" uppercase-label>
          <template #label>
            <i class="pi pi-box" />
            {{
              t('common.paramTypes', { param: t('referenceData.equipment') })
            }}
          </template>
        </KeyValuePair>
      </div>

      <KeyValuePair
        class="loadboard-card-comments"
        :label="t('common.comments', 2)"
        :value="loadboard.comments"
        uppercase-label
      />
    </template>
  </Card>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { translateEnum } from '@rygen/rygen-ui/i18n';
import { Severity } from '@rygen/rygen-ui/model';

import datImg from '@/assets/img/dat_logo.png';
import truckstopImg from '@/assets/img/truckstop_logo.jpg';
import { Loadboard, LoadboardSource } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Card from 'primevue/card';

const props = defineProps({
  loadboard: {
    type: Object as PropType<Loadboard>,
    required: true,
  },
});

const emit = defineEmits<{
  (event: 'remove', source: LoadboardSource): void;
}>();

const vTooltip = useTooltip();
const { t, d } = useI18n();

const removeTooltip = computed(() => {
  switch (props.loadboard.source) {
    case LoadboardSource.DAT:
      return t('rateShop.removeFromDat');
    case LoadboardSource.TRUCKSTOP:
      return t('rateShop.removeFromTruckstop');
    default:
      return t('rateShop.removeLoadboard');
  }
});

const sourceImage = computed(() => {
  switch (props.loadboard.source) {
    case LoadboardSource.DAT:
      return datImg;
    case LoadboardSource.TRUCKSTOP:
      return truckstopImg;
    default:
      return '';
  }
});
</script>

<style>
.loadboard-card.p-card {
  min-width: 250px;
  max-width: 380px;
  flex: 1 1 250px;
  border-radius: var(--p-border-radius-sm);
  border: 1px solid var(--p-gray-200);

  .p-card-body {
    padding: 0;

    .p-card-content {
      display: flex;
      flex-direction: column;
      padding: var(--sp-4);
    }
  }

  .loadboard-card-header {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    padding-bottom: var(--sp-3);
    border-bottom: var(--divider);
  }

  .loadboard-card-logo-wrapper {
    display: flex;
    align-items: center;
    height: var(--sp-12);
    flex-shrink: 0;
  }

  .loadboard-card-logo {
    width: auto;

    &.loadboard-card-logo--dat {
      height: var(--sp-6);
    }

    &.loadboard-card-logo--truckstop {
      height: var(--sp-12);
    }
  }

  .loadboard-card-expires {
    color: var(--p-gray-500);
  }

  .loadboard-card-remove {
    margin-left: auto;
  }

  .key-value-pair {
    .key-value-pair-label {
      color: var(--p-gray-500);
    }

    .key-value-pair-value {
      color: var(--p-gray-600);
      font-size: var(--font-size);
    }
  }

  .loadboard-card-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--sp-3);
    padding-top: var(--sp-4);
  }

  .loadboard-card-comments {
    margin-top: var(--sp-3);

    .key-value-pair-value {
      color: var(--p-gray-500);
    }
  }
}
</style>
